import React from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import DrumPad from "./DrumPad";
import { ASSETS, SCHEDULER_URL } from "../lib/constants";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Hero() {
  // cursor position, normalized to -0.5 … 0.5, smoothed with a spring for depth parallax
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18, mass: 0.6 });
  const sy = useSpring(my, { stiffness: 50, damping: 18, mass: 0.6 });

  const onMove = (e) => {
    mx.set(e.clientX / window.innerWidth - 0.5);
    my.set(e.clientY / window.innerHeight - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };

  // layered depth — closer layers move more
  const vidX = useTransform(sx, (v) => v * -22);
  const vidY = useTransform(sy, (v) => v * -16);
  const glowX = useTransform(sx, (v) => v * 70);
  const glowY = useTransform(sy, (v) => v * 70);
  const orbX = useTransform(sx, (v) => v * -60);
  const orbY = useTransform(sy, (v) => v * -48);
  const headX = useTransform(sx, (v) => v * 18);
  const headY = useTransform(sy, (v) => v * 14);

  return (
    <section
      className="relative min-h-screen w-full overflow-hidden flex items-end"
      data-testid="hero-section"
      onMouseMove={onMove}
      onMouseLeave={reset}
    >
      <motion.video
        className="absolute inset-0 w-full h-full object-cover"
        style={{ x: vidX, y: vidY, scale: 1.08 }}
        autoPlay
        muted
        loop
        playsInline
        poster={ASSETS.heroFallback}
        data-testid="hero-video"
      >
        <source src={ASSETS.heroVideo} type="video/mp4" />
      </motion.video>
      <div className="absolute inset-0 bg-gradient-to-t from-bg via-bg/70 to-bg/30" />
      <motion.div
        className="absolute inset-0 pointer-events-none"
        style={{ x: glowX, y: glowY, background: "radial-gradient(120% 80% at 50% 100%, rgba(var(--glow),0.15), transparent 60%)" }}
      />
      {/* floating depth orb */}
      <motion.div
        className="absolute -top-10 right-[8%] w-[28rem] h-[28rem] rounded-full pointer-events-none blur-[120px]"
        style={{ x: orbX, y: orbY, background: "radial-gradient(circle, rgba(var(--glow),0.28), transparent 70%)" }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10 pb-16 md:pb-24 pt-28">
        <motion.div custom={0} variants={fade} initial="hidden" animate="show" className="mb-6">
          <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2.5 rounded-full border border-primary-c/40 bg-primary-c/10 backdrop-blur-md px-4 py-2 shadow-[0_4px_30px_rgba(var(--glow),0.3)]">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-primary-c opacity-60 animate-ping" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-primary-c" />
            </span>
            <span className="font-mono text-[11px] md:text-xs tracking-[0.28em] uppercase text-primary-c font-medium">
              A Gentle Path Out of Overwhelm
            </span>
          </span>
        </motion.div>

        {/* headline + sub get a subtle parallax float (wrapper transformed, inner keeps entrance) */}
        <motion.div style={{ x: headX, y: headY }}>
          <motion.h1 custom={1} variants={fade} initial="hidden" animate="show" className="font-serif font-normal text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight max-w-4xl">
            Rhythm that heals.
          </motion.h1>
          <motion.p custom={2} variants={fade} initial="hidden" animate="show" className="mt-6 max-w-2xl text-base md:text-lg text-fg/80 leading-relaxed">
            Gentle, rhythm-based sessions that <span className="text-primary-c font-medium">quiet an overwhelmed nervous system</span> — a steady path back to <span className="text-primary-c font-medium">calm and ease</span>.
          </motion.p>
        </motion.div>

        <motion.div custom={3} variants={fade} initial="hidden" animate="show" className="mt-10">
          <DrumPad />
        </motion.div>

        <motion.div custom={4} variants={fade} initial="hidden" animate="show" className="mt-10 flex flex-wrap gap-4">
          <a
            href={SCHEDULER_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-book-btn"
            className="shape-r bg-primary-c text-bg font-medium px-7 py-3.5 hover:opacity-90 transition-opacity"
          >
            Begin Your Release
          </a>
          <a
            href="#reframe"
            data-testid="hero-explore-btn"
            className="shape-r border border-white/25 text-fg font-medium px-7 py-3.5 hover:bg-white/10 transition-colors"
          >
            Feel How It Works
          </a>
        </motion.div>
      </div>
    </section>
  );
}
